import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary';
import Overview from './Overview';
import Reports from './reports/Reports';
import CorrespondenceRegister from './correspondence/CorrespondenceRegister';
import ThreadDetail from './correspondence/ThreadDetail';
import RABillsRegister from './financials/RABillsRegister';
import BankGuarantees from './financials/BankGuarantees';
import MilestoneTracker from './financials/MilestoneTracker';
import DrawingsDesign from './drawings/DrawingsDesign';
import Projects from './admin/Projects';
import Departments from './admin/Departments';
import UsersAccessShell from './admin/usersAccess/UsersAccessShell';
import ConfigEngineShell from './admin/configEngine/ConfigEngineShell';
import RaBillTemplatesShell from './admin/raBillTemplates/RaBillTemplatesShell';
import ProjectScope from './scope/ProjectScope';
import ProjectHandOver from './scope/ProjectHandOver';
import SiteOffice from './scope/SiteOffice';
import ClientOffice from './scope/ClientOffice';
import Deviations from './technical/Deviations';
import SpecializedItems from './technical/SpecializedItems';
import QAQC from './technical/QAQC';
import Clearance from './technical/Clearance';
import SubmittalDashboard from './submittals/SubmittalDashboard';
import SubmittalRegister from './submittals/SubmittalRegister';
import CreateSubmittal from './submittals/CreateSubmittal';
import SubmittalDetail from './submittals/SubmittalDetail';
import ApprovalInbox from './submittals/ApprovalInbox';
import RevisionRegister from './submittals/RevisionRegister';
import SubmittalReports from './submittals/SubmittalReports';
import SubmittalTemplatesAdmin from './submittals/SubmittalTemplatesAdmin';
import UserGuide from './UserGuide';
import {
  APP_PRODUCT_NAME,
  COMPANY_LOGO,
  POWERED_BY_LOGO,
  POWERED_BY_NAME,
} from '../config/brandingConfig';
import PwaInstallPrompt from '../components/PwaInstallPrompt';
import './Dashboard.css';

/** Set true to show Submittal Portal in the sidebar again. */
const ENABLE_SUBMITTAL_PORTAL = false;

const PAGE_PERMISSION_BY_TAB = {
  reports: { moduleKey: 'reports', actionKey: 'page:reports_dashboard' },
  'correspondence-all': { moduleKey: 'correspondence', actionKey: 'page:register' },
  'correspondence-open': { moduleKey: 'correspondence', actionKey: 'page:open_targeted_reply_overdue' },
  'correspondence-critical': { moduleKey: 'correspondence', actionKey: 'page:critical' },
  'correspondence-closed': { moduleKey: 'correspondence', actionKey: 'page:closed' },
  'thread-detail': { moduleKey: 'correspondence', actionKey: 'view' },
  'ra-bills': { moduleKey: 'financials', actionKey: 'page:ra_bills_register' },
  'bank-guarantees': { moduleKey: 'financials', actionKey: 'page:bg_insurance_bonds' },
  milestones: { moduleKey: 'financials', actionKey: 'page:milestone_eot_requests' },
  'drawings-design': { moduleKey: 'technical', actionKey: 'page:drawings_design' },
  deviations: { moduleKey: 'technical', actionKey: 'page:deviations_extra_items' },
  'specialized-items': { moduleKey: 'technical', actionKey: 'page:specialized_items' },
  qaqc: { moduleKey: 'technical', actionKey: 'page:qaqc' },
  clearance: { moduleKey: 'technical', actionKey: 'page:clearance' },
  'project-scope': { moduleKey: 'scope', actionKey: 'page:project_scope' },
  'project-handover': { moduleKey: 'scope', actionKey: 'page:project_hand_over' },
  'site-office': { moduleKey: 'scope', actionKey: 'page:site_office' },
  'client-office': { moduleKey: 'scope', actionKey: 'page:client_office' },
  'admin-projects': { moduleKey: 'admin_panel', actionKey: 'page:projects_list' },
  'admin-users': { moduleKey: 'admin_panel', actionKey: 'page:users_roles' },
  'admin-users-access': { moduleKey: 'admin_panel', actionKey: 'page:users_roles' },
  'admin-consultants': { moduleKey: 'admin_panel', actionKey: 'page:consultants' },
  'admin-assignments': { moduleKey: 'admin_panel', actionKey: 'page:project_assignments' },
  'admin-departments': { moduleKey: 'admin_panel', actionKey: 'page:department_master' },
  'admin-roles': { moduleKey: 'admin_panel', actionKey: 'page:roles_rbac' },
  'admin-audit-logs': { moduleKey: 'admin_panel', actionKey: 'page:audit_logs' },
  'admin-config': { moduleKey: 'admin_config', actionKey: 'page:page_registry' },
  'admin-ra-templates': { moduleKey: 'ra_bill_templates', actionKey: 'page:templates' },
  'submittal-dashboard': { moduleKey: 'submittal', actionKey: 'page:submittal-dashboard' },
  'submittal-register': { moduleKey: 'submittal', actionKey: 'page:submittal-register' },
  'submittal-create': { moduleKey: 'submittal', actionKey: 'page:submittal-create' },
  'submittal-approval-inbox': { moduleKey: 'submittal', actionKey: 'page:submittal-approval-inbox' },
  'submittal-revisions': { moduleKey: 'submittal', actionKey: 'page:submittal-revisions' },
  'submittal-reports': { moduleKey: 'submittal', actionKey: 'page:submittal-reports' },
  'submittal-templates': { moduleKey: 'submittal', actionKey: 'page:submittal-templates' },
  'submittal-detail': { moduleKey: 'submittal', actionKey: 'view' },
};

const Dashboard = () => {
  const getInitialTab = () => {
    const stored = localStorage.getItem('cc_active_tab');
    return stored || 'overview';
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [selectedChainId, setSelectedChainId] = useState(null);
  const [threadFocusEntryId, setThreadFocusEntryId] = useState(null);
  /** Correspondence sidebar tab to restore when leaving thread detail (preserves which register view user was on). */
  const [correspondenceReturnTab, setCorrespondenceReturnTab] = useState('correspondence-all');
  const [selectedSubmittalId, setSelectedSubmittalId] = useState(null);
  const [submittalReturnTab, setSubmittalReturnTab] = useState('submittal-register');
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showCompanyLogo, setShowCompanyLogo] = useState(true);
  const [showPoweredByLogo, setShowPoweredByLogo] = useState(true);
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const roleCodes = Array.isArray(user.role_codes) ? user.role_codes.map((x) => String(x).toUpperCase()) : [];
  const userRole = (user.role || roleCodes[0] || '').toUpperCase();
  const userPermissionKeys = Array.isArray(user.permissions) ? user.permissions : null;
  const hasPermissionPayload = Array.isArray(user.permissions);
  const userPermissionSet = new Set((userPermissionKeys || []).map((x) => String(x)));
  const isPrivilegedUser = roleCodes.includes('SUPER_ADMIN') || roleCodes.includes('ADMIN') || userRole === 'SUPER_ADMIN' || userRole === 'ADMIN';

  const hasPermission = (moduleKey, actionKey) => {
    if (isPrivilegedUser) return true;
    if (!hasPermissionPayload) return true;
    const mk = String(moduleKey || '');
    const ak = String(actionKey || '');
    if (
      userPermissionSet.has(`${mk}:${ak}`)
      || userPermissionSet.has(`${mk}:manage`)
      || userPermissionSet.has('*:*')
    ) {
      return true;
    }
    // Page-level keys (page:*) are required for each sidebar item, but roles often only
    // grant module CRUD (view/create/update/delete) — same keys the API uses via ensureModuleAccess.
    // Treat any module CRUD grant as access to all pages in that module (except admin_panel,
    // where access is manage or explicit page:* only).
    if (ak.startsWith('page:') && mk && mk !== 'admin_panel') {
      return ['view', 'create', 'update', 'delete'].some((a) => userPermissionSet.has(`${mk}:${a}`));
    }
    return false;
  };

  const canOpenTab = (tabKey) => {
    if (tabKey === 'overview' || tabKey === 'user-guide') return true;
    if (!ENABLE_SUBMITTAL_PORTAL && String(tabKey || '').startsWith('submittal-')) return false;
    if (tabKey === 'admin-config') {
      return hasPermission('admin_config', 'page:page_registry')
        || hasPermission('admin_config', 'view')
        || hasPermission('admin_config', 'manage')
        || hasPermission('admin_panel', 'manage');
    }
    if (tabKey === 'admin-ra-templates') {
      return hasPermission('ra_bill_templates', 'page:templates')
        || hasPermission('ra_bill_templates', 'view')
        || hasPermission('ra_bill_templates', 'manage')
        || hasPermission('admin_panel', 'manage');
    }
    const requirement = PAGE_PERMISSION_BY_TAB[tabKey];
    if (!requirement) return true;
    return hasPermission(requirement.moduleKey, requirement.actionKey);
  };

  const canAccessAdminPanel = hasPermission('admin_panel', 'manage');

  useEffect(() => {
    if (!canOpenTab(activeTab)) {
      setActiveTab('overview');
    }
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('cc_active_tab', activeTab);
  }, [activeTab]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleViewThread = (chainId, opts) => {
    if (typeof activeTab === 'string' && activeTab.startsWith('correspondence-')) {
      setCorrespondenceReturnTab(activeTab);
    }
    setSelectedChainId(chainId);
    const eid = opts && opts.entryId != null ? Number(opts.entryId) : null;
    setThreadFocusEntryId(Number.isFinite(eid) && eid > 0 ? eid : null);
    setActiveTab('thread-detail');
  };

  const handleBackToRegister = () => {
    setSelectedChainId(null);
    setThreadFocusEntryId(null);
    setActiveTab(correspondenceReturnTab || 'correspondence-all');
  };

  const handleViewSubmittal = (submittalId) => {
    if (typeof activeTab === 'string' && activeTab.startsWith('submittal-') && activeTab !== 'submittal-detail') {
      setSubmittalReturnTab(activeTab);
    }
    setSelectedSubmittalId(submittalId);
    setActiveTab('submittal-detail');
  };

  const handleBackToSubmittalRegister = () => {
    setSelectedSubmittalId(null);
    setActiveTab(submittalReturnTab || 'submittal-register');
  };

  const navGroups = [
    {
      label: 'Overview',
      items: [
        { key: 'overview', label: 'Overview', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
          </svg>
        )},
        { key: 'reports', label: 'Reports', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
          </svg>
        )},
      ],
    },
    {
      label: 'Correspondence',
      items: [
        { key: 'correspondence-all', label: 'Correspondence Register', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        )},
        { key: 'correspondence-open', label: 'Open / Targeted Reply Overdue', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        )},
        { key: 'correspondence-critical', label: 'Critical', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        )},
        { key: 'correspondence-closed', label: 'Closed', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        )},
      ],
    },

    {
      label: 'Submittal Portal',
      items: [
        { key: 'submittal-dashboard', label: 'Submittal Dashboard', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
          </svg>
        )},
        { key: 'submittal-register', label: 'Submittal Register', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
          </svg>
        )},
        { key: 'submittal-create', label: 'Create Submittal', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        )},
        { key: 'submittal-approval-inbox', label: 'Approval Inbox', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 12h-6l-2 3H10l-2-3H2" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
          </svg>
        )},
        { key: 'submittal-revisions', label: 'Revision Register', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
        )},
        { key: 'submittal-reports', label: 'Submittal Reports', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
          </svg>
        )},
        { key: 'submittal-templates', label: 'Templates Admin', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
          </svg>
        )},
      ],
    },
    {
      label: 'Financials & Compliance',
      items: [
        { key: 'ra-bills', label: 'RA Bills Register', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 2h12v20l-2-1.5L14 22l-2-1.5L10 22l-2-1.5L6 22V2z" />
            <line x1="9" y1="8" x2="15" y2="8" />
            <line x1="9" y1="12" x2="15" y2="12" />
            <line x1="9" y1="16" x2="13" y2="16" />
          </svg>
        )},
        { key: 'bank-guarantees', label: 'BG / Insurance Bonds', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
          </svg>
        )},
        { key: 'milestones', label: 'Milestone/EOT Requests', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
        )},
      ],
    },
    {
      label: 'Technical & Execution',
      items: [
        { key: 'drawings-design', label: 'Drawings & Design', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <path d="M12 18v-6" /><path d="M9 15l3-3 3 3" />
          </svg>
        )},
        { key: 'deviations', label: 'Deviations & Extra Items', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        )},
        { key: 'specialized-items', label: 'Specialized Items', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 7h-9" /><path d="M14 17H5" /><circle cx="17" cy="17" r="3" /><circle cx="7" cy="7" r="3" />
          </svg>
        )},
        { key: 'qaqc', label: 'QAQC', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
        )},
        { key: 'clearance', label: 'Clearance', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 12l2 2 4-4" />
            <circle cx="12" cy="12" r="9" />
          </svg>
        )},
      ],
    },
    {
      label: 'Agreement & Scope',
      items: [
        { key: 'project-scope', label: 'Project Scope', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
          </svg>
        )},
        { key: 'project-handover', label: 'Project Hand Over', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><polyline points="17 11 19 13 23 9" />
          </svg>
        )},
        { key: 'site-office', label: 'Site Office', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        )},
        { key: 'client-office', label: 'Client Office', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
          </svg>
        )},
      ],
    },
    {
      label: 'Help & Support',
      items: [
        {
          key: 'user-guide',
          label: 'User Guide',
          icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          ),
        },
      ],
    },
    canAccessAdminPanel ? {
      label: 'Admin Panel',
      items: [
        { key: 'admin-projects', label: 'Projects List', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
          </svg>
        )},
        { key: 'admin-users-access', label: 'Users & Access', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
          </svg>
        )},
        { key: 'admin-departments', label: 'Department Master', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="6" rx="1" />
            <rect x="3" y="14" width="18" height="6" rx="1" />
          </svg>
        )},
        { key: 'admin-config', label: 'Page, Form & KPI Configuration', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        )},
        { key: 'admin-ra-templates', label: 'RA Bill Templates', icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="8" y1="13" x2="16" y2="13" />
            <line x1="8" y1="17" x2="16" y2="17" />
          </svg>
        )},
      ],
    } : null,
  ].filter(Boolean);
  const filteredNavGroups = navGroups
    .filter((group) => ENABLE_SUBMITTAL_PORTAL || group.label !== 'Submittal Portal')
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => canOpenTab(item.key)),
    }))
    .filter((group) => group.items.length > 0);

  const handleCorrespondenceRegisterNav = (targetFilter) => {
    const tabByFilter = {
      all: 'correspondence-all',
      open: 'correspondence-open',
      critical: 'correspondence-critical',
      closed: 'correspondence-closed',
    };
    const nextTab = tabByFilter[targetFilter];
    if (nextTab) setActiveTab(nextTab);
  };

  const renderContent = () => {
    if (!canOpenTab(activeTab)) {
      return <Overview />;
    }
    switch (activeTab) {
      case 'overview':
        return (
          <Overview
            onNavigate={setActiveTab}
            onSelectProject={(id) => {
              setSelectedProjectId(id);
              setActiveTab('project-scope');
            }}
          />
        );
      case 'reports':
        return <Reports />;
      case 'correspondence-all':
        return <CorrespondenceRegister filter="all" onViewThread={handleViewThread} onNavigateRegisterFilter={handleCorrespondenceRegisterNav} />;
      case 'correspondence-open':
        return <CorrespondenceRegister filter="open" onViewThread={handleViewThread} onNavigateRegisterFilter={handleCorrespondenceRegisterNav} />;
      case 'correspondence-critical':
        return <CorrespondenceRegister filter="critical" onViewThread={handleViewThread} onNavigateRegisterFilter={handleCorrespondenceRegisterNav} />;
      case 'correspondence-closed':
        return <CorrespondenceRegister filter="closed" onViewThread={handleViewThread} onNavigateRegisterFilter={handleCorrespondenceRegisterNav} />;
      case 'thread-detail':
        return (
          <ThreadDetail
            chainId={selectedChainId}
            focusEntryId={threadFocusEntryId}
            onFocusEntryConsumed={() => setThreadFocusEntryId(null)}
            onBack={handleBackToRegister}
            isAdmin={canAccessAdminPanel}
            onOpenDrawingsDesign={() => setActiveTab('drawings-design')}
            onViewSubmittal={handleViewSubmittal}
          />
        );
      case 'ra-bills':
        return <RABillsRegister onViewThread={handleViewThread} isAdmin={canAccessAdminPanel} />;
      case 'bank-guarantees':
        return <BankGuarantees onViewThread={handleViewThread} />;
      case 'milestones':
        return <MilestoneTracker onViewThread={handleViewThread} />;
      case 'drawings-design':
        return <DrawingsDesign onViewThread={handleViewThread} />;
      case 'admin-projects':
        return canAccessAdminPanel ? <Projects /> : <Overview />;
      case 'admin-users-access':
      case 'admin-users':
      case 'admin-consultants':
      case 'admin-assignments':
      case 'admin-roles':
      case 'admin-audit-logs': {
        const initialByTab = {
          'admin-users-access': 'overview',
          'admin-users': 'users',
          'admin-roles': 'roles',
          'admin-assignments': 'project-access',
          'admin-consultants': 'consultants',
          'admin-audit-logs': 'audit',
        };
        return canAccessAdminPanel
          ? <UsersAccessShell canAccess={canAccessAdminPanel} initialTab={initialByTab[activeTab] || 'overview'} />
          : <Overview />;
      }
      case 'admin-departments':
        return canAccessAdminPanel ? <Departments onNavigate={setActiveTab} /> : <Overview />;
      case 'admin-config':
        return canOpenTab('admin-config')
          ? <ConfigEngineShell canAccess={canOpenTab('admin-config')} />
          : <Overview />;
      case 'admin-ra-templates':
        return canOpenTab('admin-ra-templates')
          ? <RaBillTemplatesShell canAccess={canOpenTab('admin-ra-templates')} />
          : <Overview />;
      case 'project-scope':
        return (
          <ProjectScope
            initialProjectId={selectedProjectId}
            onClearProject={() => setSelectedProjectId(null)}
            onBackToOverview={() => {
              setSelectedProjectId(null);
              setActiveTab('overview');
            }}
          />
        );
      case 'project-handover':
        return <ProjectHandOver />;
      case 'site-office':
        return <SiteOffice />;
      case 'client-office':
        return <ClientOffice />;
      case 'deviations':
        return <Deviations />;
      case 'specialized-items':
        return <SpecializedItems />;
      case 'qaqc':
        return <QAQC />;
      case 'clearance':
        return <Clearance />;
      case 'submittal-dashboard':
        return <SubmittalDashboard onNavigate={setActiveTab} onViewSubmittal={handleViewSubmittal} />;
      case 'submittal-register':
        return <SubmittalRegister onViewSubmittal={handleViewSubmittal} onCreateNew={() => setActiveTab('submittal-create')} />;
      case 'submittal-create':
        return (
          <CreateSubmittal
            onCancel={() => setActiveTab('submittal-register')}
            onCreated={(id) => { if (id) handleViewSubmittal(id); else setActiveTab('submittal-register'); }}
          />
        );
      case 'submittal-approval-inbox':
        return <ApprovalInbox onViewSubmittal={handleViewSubmittal} />;
      case 'submittal-revisions':
        return <RevisionRegister onViewSubmittal={handleViewSubmittal} />;
      case 'submittal-reports':
        return <SubmittalReports onViewSubmittal={handleViewSubmittal} />;
      case 'submittal-templates':
        return <SubmittalTemplatesAdmin />;
      case 'submittal-detail':
        return (
          <SubmittalDetail
            submittalId={selectedSubmittalId}
            onBack={handleBackToSubmittalRegister}
            onViewThread={handleViewThread}
          />
        );
      case 'user-guide':
        return <UserGuide />;
      default:
        return <Overview />;
    }
  };

  return (
    <div className="dashboard">
      <PwaInstallPrompt />
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <button className="sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <div className="header-logo-icon">
              {showCompanyLogo && (
                <img
                  src={COMPANY_LOGO}
                  alt="Company Logo"
                  onError={() => setShowCompanyLogo(false)}
                />
              )}
            </div>
            <h1>{APP_PRODUCT_NAME}</h1>
          </div>
          <div className="header-right">
            <div className="user-info">
              <div className="user-avatar">{user.name?.charAt(0) || 'U'}</div>
              <div className="user-details">
                <span className="user-name">{user.name || 'User'}</span>
                <span className="user-role">{user.role || 'USER'}</span>
              </div>
              <button className="btn-logout" onClick={handleLogout}>Logout</button>
            </div>
          </div>
        </div>
      </header>

      <div className="dashboard-container">
        <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-brand">
              <div className="sidebar-logo">
                {APP_PRODUCT_NAME.slice(0, 2).toUpperCase()}
              </div>
              <div className="sidebar-brand-text">
                <h3>{APP_PRODUCT_NAME}</h3>
                <span>{user.role || 'User'}</span>
              </div>
            </div>
            <button
              className="sidebar-collapse-btn"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                {sidebarCollapsed ? (
                  <polyline points="15 18 9 12 15 6" />
                ) : (
                  <polyline points="9 18 15 12 9 6" />
                )}
              </svg>
            </button>
          </div>
          <nav className="sidebar-nav">
            {filteredNavGroups.map((group) => (
              group.label === 'Admin Panel' && !canAccessAdminPanel ? null : (
              <div key={group.label} className="nav-group">
                <div className="nav-group-label">{group.label}</div>
                {group.items.map((item) => (
                  <button
                    key={item.key}
                    className={`nav-item ${activeTab === item.key ? 'active' : ''}`}
                    onClick={() => {
                      setActiveTab(item.key);
                      if (item.key !== 'thread-detail') setSelectedChainId(null);
                      if (item.key !== 'submittal-detail') setSelectedSubmittalId(null);
                    }}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
              )
            ))}
          </nav>
        </aside>

        <main className="dashboard-content">
          <ErrorBoundary>{renderContent()}</ErrorBoundary>
        </main>
      </div>

      <footer className="app-footer">
        {showPoweredByLogo && (
          <img
            src={POWERED_BY_LOGO}
            alt={`${POWERED_BY_NAME} Logo`}
            className="footer-logo"
            onError={() => setShowPoweredByLogo(false)}
          />
        )}
        <span>Powered by</span>
        <span className="footer-brand">{POWERED_BY_NAME}</span>
      </footer>
    </div>
  );
};

export default Dashboard;
